import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Play, Star, CheckCircle, Clock, Users, Award, TrendingUp, Zap, BookOpen, Sparkles, Target, ChevronDown, ChevronUp, Shield, ArrowRight, Rocket, Code, Image, Video, Mic } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { siteVideoService } from '@/services/siteVideoService';
import { setEnrollmentProduct } from '@/redux/actions/enrollment';
import { trackCTA, trackVideo, trackPageView, trackAccordion } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { useProductPricing, formatPrice } from '@/hooks/useFeaturedPricing';
import { metaPixel } from '@/services/metaPixel';

export default function AIToolsAccelerator() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Fetch dynamic pricing from API - SINGLE SOURCE OF TRUTH
  const {
    loading: pricingLoading,
    displayPrice,
    originalPrice,
    isSaleActive,
    savingsAmount,
    savingsPercentage
  } = useProductPricing('ai-tools-accelerator', 'course');

  useScrollTracking('AIToolsAccelerator');

  // State for FAQ expansion
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Handle FAQ click with tracking
  const handleFaqClick = (idx: number, question: string) => {
    const isExpanding = expandedFaq !== idx;
    setExpandedFaq(isExpanding ? idx : null);
    if (isExpanding) {
      trackAccordion.questionExpand("faq-aita-" + idx, question, 'AIToolsAccelerator');
    }
  };

  useEffect(() => {
    // Meta Pixel
    metaPixel.init();
    metaPixel.pageView();
    trackPageView('AIToolsAccelerator');
  }, []);

  // Fetch AITA hero video
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const videoData = await siteVideoService.getSiteVideo('aita_hero');
        if (videoData.playback_url) {
          setVideoUrl(videoData.playback_url);
        }
      } catch (error) {
      }
    };

    fetchVideo();
  }, []);

  // Handle enrollment click
  const handleEnrollClick = () => {
    trackCTA.enrollCtaClick('ai-tools-accelerator', 'hero', 97);
    // Set enrollment product using slug (stable identifier)
    dispatch(setEnrollmentProduct('ai-tools-accelerator', 'course') as any);
    navigate('/enrollment');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>AI Tools Accelerator - AI Mastery Hub</title>
        <meta name="description" content="Master 20+ essential AI tools for work, business, and automation. From ChatGPT to Midjourney, HeyGen to Claude Code." />
      </Helmet>

      <Navigation />

      {/* Hero Section with Video */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-cyan-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-sm mb-6">
              <span className="text-blue-400 font-semibold">🚀 TRENDING • UPDATED FOR 2025</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="block">AI Tools</span>
              <span className="block bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
                Accelerator
              </span>
            </h1>
            <p className="text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Master <span className="text-blue-400 font-semibold">20+ Cutting-Edge AI Tools</span> That Are Reshaping Every Industry. Build Complete Workflows in Days, Not Months
            </p>

            {/* Social Proof */}
            <div className="flex items-center justify-center gap-8 mb-12">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 border-2 border-black"></div>
                  ))}
                </div>
                <span className="text-sm text-gray-400">Growing enrollment</span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
                <span className="text-sm text-gray-400 ml-2">8 hours hands-on training</span>
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="max-w-5xl mx-auto mb-12">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-all"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-4 border border-white/20">
                <div className="relative aspect-video bg-gradient-to-br from-blue-900/50 to-cyan-900/50 rounded-2xl overflow-hidden">
                  {videoUrl ? (
                    <video
                      src={videoUrl}
                      controls
                      onPlay={() => trackVideo.play('aita-hero-video', videoUrl, 'AIToolsAccelerator')}
                      className="w-full h-full object-cover"
                      poster="https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1200&h=675&fit=crop"
                    />
                  ) : (
                    <>
                      <img
                        src="https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=1200&h=675&fit=crop"
                        alt="Course Preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-all cursor-pointer group">
                        <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-2xl">
                          <Play className="w-8 h-8 text-black fill-black ml-1" />
                        </div>
                      </div>
                      <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                        <span className="text-sm font-semibold">4:15 Preview</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleEnrollClick}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 px-12 py-5 rounded-xl font-bold text-xl flex items-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/50 group"
            >
              <span>Enroll Now - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            {isSaleActive && savingsAmount > 0 && (
              <div className="text-center">
                <div className="text-2xl text-gray-300 line-through font-medium decoration-2">{formatPrice(originalPrice)}</div>
                <div className="text-sm text-blue-400">Save {savingsPercentage}% Today</div>
              </div>
            )}
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Lifetime Access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Expert Support</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Certificate Included</span>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Showcase */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-900/10 via-black to-cyan-900/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Master These Industry-Leading Tools</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              20+ tools across 8 essential categories - everything you need for complete AI-powered workflows
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Sparkles, title: 'LLMs', tools: 'ChatGPT, Claude, Gemini' },
              { icon: Code, title: 'Development', tools: 'Claude Code, Lovable' },
              { icon: Image, title: 'Image Gen', tools: 'Midjourney, Flux, FAL.ai' },
              { icon: Video, title: 'Video AI', tools: 'Veo.3, HeyGen, Kling' },
              { icon: Mic, title: 'Voice AI', tools: 'ElevenLabs, OpenAI TTS' },
              { icon: BookOpen, title: 'Presentations', tools: 'Gamma.app, SlideSpeak' },
              { icon: Zap, title: 'Automation', tools: 'OpenPhone, Nano Banana' },
              { icon: Rocket, title: 'Integration', tools: 'Complete Workflows' }
            ].map((category, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-blue-500/50 transition-all group">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <category.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold mb-2">{category.title}</h3>
                <p className="text-sm text-gray-400">{category.tools}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Problem */}
            <div>
              <h2 className="text-4xl font-bold mb-6 text-red-400">The Problem</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>You're overwhelmed by the explosion of new AI tools</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>You don't know which tools to use for which tasks</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>You're stuck using only one or two tools inefficiently</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p>You lack the knowledge to build complete AI workflows</p>
                </div>
              </div>
            </div>

            {/* Solution */}
            <div>
              <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">The Solution</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <p>Master 20+ essential AI tools with hands-on training</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <p>Learn exactly when and how to use each tool effectively</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <p>Build complete workflows: content, marketing, development</p>
                </div>
                <div className="flex items-start gap-3 text-gray-300">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <p>Get $2000+ in templates and integration guides</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Master */}
      <section className="py-24 px-6 bg-gradient-to-br from-blue-900/10 via-black to-cyan-900/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Complete Tool Mastery</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              8 comprehensive modules covering the entire AI tools ecosystem
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'LLMs Deep Dive',
                desc: 'Master ChatGPT, Claude, and Gemini. Learn when to use each, advanced features, and power-user techniques.',
                tools: ['ChatGPT-4', 'Claude', 'Gemini']
              },
              {
                title: 'AI Development Tools',
                desc: 'Build websites and apps in minutes with Lovable and Claude Code. No coding experience required.',
                tools: ['Lovable', 'Claude Code']
              },
              {
                title: 'Image Generation',
                desc: 'Create stunning visuals with Midjourney, Flux, and FAL.ai. Master prompting, styles, and consistency.',
                tools: ['Midjourney', 'Flux Lora', 'FAL.ai']
              },
              {
                title: 'Video & Avatars',
                desc: 'Generate AI videos and avatars with Veo.3, HeyGen, and Kling. Create professional content at scale.',
                tools: ['Veo.3', 'HeyGen', 'Kling']
              },
              {
                title: 'Voice AI',
                desc: 'Clone voices and generate speech with ElevenLabs and OpenAI TTS. Create audiobooks, podcasts, voiceovers.',
                tools: ['ElevenLabs', 'OpenAI TTS']
              },
              {
                title: 'Presentations',
                desc: 'Build stunning decks in seconds with Gamma.app. Convert documents to interactive presentations.',
                tools: ['Gamma.app', 'SlideSpeak']
              },
              {
                title: 'Complete Workflows',
                desc: 'Combine multiple tools for content creation, marketing campaigns, and product development.',
                tools: ['End-to-End', 'Integration']
              },
              {
                title: 'Stay Current',
                desc: 'Learn strategies for discovering and evaluating new AI tools as the landscape evolves.',
                tools: ['Research', 'Updates']
              }
            ].map((module, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-500/50 transition-all group">
                <h3 className="text-2xl font-bold mb-3 group-hover:text-blue-400 transition-colors">{module.title}</h3>
                <p className="text-gray-300 mb-4 leading-relaxed">{module.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {module.tools.map((tool, toolIdx) => (
                    <span key={toolIdx} className="px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-xs font-medium text-blue-400">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Preview */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Course Curriculum</h2>
            <p className="text-xl text-gray-400">8 hours of expert training • 8 modules • 24 lessons</p>
          </div>

          <div className="space-y-4">
            {[
              { module: 1, title: 'Introduction to AI Tools Ecosystem', duration: '30 min', lessons: 2 },
              { module: 2, title: 'Mastering Core LLMs', duration: '75 min', lessons: 3 },
              { module: 3, title: 'AI-Powered Development & Site Building', duration: '60 min', lessons: 2 },
              { module: 4, title: 'AI-Generated Media: Images & Video', duration: '90 min', lessons: 2 },
              { module: 5, title: 'AI Avatars & Voice Generation', duration: '60 min', lessons: 2 },
              { module: 6, title: 'Presentation & Document AI Tools', duration: '45 min', lessons: 2 },
              { module: 7, title: 'Building Complete AI Workflows', duration: '75 min', lessons: 3 },
              { module: 8, title: 'Advanced Strategies & Capstone', duration: '45 min', lessons: 3 }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-blue-500/30 transition-all group cursor-pointer">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center font-bold">
                      {item.module}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg group-hover:text-blue-400 transition-colors">{item.title}</h3>
                      <p className="text-sm text-gray-400">{item.lessons} lessons • {item.duration}</p>
                    </div>
                  </div>
                  <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 bg-gradient-to-br from-blue-900/10 via-black to-cyan-900/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Student Success Stories</h2>
            <p className="text-xl text-gray-400">See how mastering AI tools transformed their work</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'David Kim',
                role: 'Content Creator',
                image: 'from-blue-400 to-indigo-400',
                text: 'I went from spending 20 hours per week creating content to just 5 hours. The workflow templates alone saved me hundreds of hours.'
              },
              {
                name: 'Jessica Martinez',
                role: 'Startup Founder',
                image: 'from-cyan-400 to-teal-400',
                text: 'We built our entire marketing campaign using the tools from this course. Saved $10k+ on agencies and designers.'
              },
              {
                name: 'Robert Taylor',
                role: 'Freelancer',
                image: 'from-green-400 to-emerald-400',
                text: 'I\'ve 3x my client base by offering AI-powered services. This course gave me the complete toolkit I needed.'
              }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${testimonial.image}`}></div>
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-blue-900/20 via-black to-cyan-900/20">
        <div className="max-w-4xl mx-auto">
          <div className="relative group">
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-all animate-pulse"></div>
            <div className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl border-2 border-white/30 p-12">
              <div className="text-center mb-8">
                {isSaleActive && savingsAmount > 0 && (
                  <div className="inline-block px-6 py-2 bg-gradient-to-r from-blue-600/30 to-cyan-600/30 border border-blue-500/50 rounded-full mb-4">
                    <span className="text-sm font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                      ⏰ LIMITED TIME OFFER
                    </span>
                  </div>
                )}
                <h2 className="text-5xl font-bold mb-4">Start Mastering AI Tools Today</h2>
                <p className="text-xl text-gray-300">Join students building with AI</p>
              </div>

              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="text-center">
                  <div className="text-6xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                    {pricingLoading ? '...' : formatPrice(displayPrice)}
                  </div>
                  {isSaleActive && savingsAmount > 0 && (
                    <>
                      <div className="text-3xl text-gray-300 line-through font-semibold decoration-2 mb-2">{formatPrice(originalPrice)}</div>
                      <div className="text-lg text-green-400 font-semibold">Save {formatPrice(savingsAmount)} ({savingsPercentage}% OFF)</div>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">8 hours of hands-on training</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">20+ tools & integration guides</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">Lifetime access & updates</span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">$2000+ in templates</span>
                </div>
              </div>

              <button
                onClick={handleEnrollClick}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 py-6 rounded-xl font-bold text-2xl flex items-center justify-center space-x-3 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/50 group mb-6"
              >
                <span>Enroll Now - {pricingLoading ? '...' : formatPrice(displayPrice)}</span>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="flex items-center justify-center gap-2 text-gray-400">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm">Secure Payment • Instant Access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'Do I need to pay for all these AI tools separately?',
                a: 'Many have free tiers that you can start with. We show you exactly which features are free vs paid, and when it makes sense to upgrade.'
              },
              {
                q: 'Is this course updated as new tools emerge?',
                a: 'Yes! You get lifetime updates. As the AI landscape evolves, we add new tools and update existing modules.'
              },
              {
                q: 'I\'m not technical. Can I still learn these tools?',
                a: 'Absolutely! We designed this for non-technical users. Everything is explained step-by-step with visual examples.'
              },
              {
                q: 'What if a tool becomes outdated?',
                a: 'We constantly monitor the AI landscape and update the course. Plus, the principles you learn apply to any new tools that emerge.'
              },
              {
                q: 'Do I get templates and resources?',
                a: 'Yes! You get workflow templates, integration guides, prompts, and more - worth over $2000 in resources.'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => handleFaqClick(idx, faq.q)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <h3 className="text-xl font-bold text-blue-400 pr-4">{faq.q}</h3>
                  {expandedFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === idx && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-300 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
